import {
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCol,
  CDropdown,
  CDropdownMenu,
  CDropdownToggle,
  CFormInput,
  CFormSelect,
  CInputGroup,
  CInputGroupText,
  CRow,
  CSmartTable,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilArrowBottom,
  cilArrowTop,
  cilBadge,
  cilColumns,
  cilPeople,
  cilSearch,
  cilStar,
} from '@coreui/icons'
import React, { useEffect, useState } from 'react'
import PlayerService from 'src/service/PlayerService'
import { resolvePlayerImageUrl } from 'src/utils/imageUrl'

const SEGMENTS = [
  { key: 'summary', label: 'Summary' },
  { key: 'batting', label: 'Batting' },
  { key: 'bowling', label: 'Bowling' },
  { key: 'fielding', label: 'Fielding' },
]

const SUMMARY_COLUMNS = [
  { label: 'Player', filter: false, sorter: true, key: 'player_name' },
  { label: 'Team', filter: false, sorter: false, key: 'team_name' },
  { label: 'Position', filter: false, sorter: true, key: 'position_name' },
  { label: 'Matches', filter: false, sorter: true, key: 'matches' },
  { label: 'Fantasy Points', filter: false, sorter: true, key: 'sum_fp' },
  { label: 'Runs', filter: false, sorter: true, key: 'sum_rs' },
  { label: 'Wickets', filter: false, sorter: true, key: 'sum_wks' },
  { label: 'Catches', filter: false, sorter: true, key: 'sum_cs' },
  { label: 'Current Value', filter: false, sorter: true, key: 'current_value' },
]

const BATTING_COLUMNS = [
  { label: 'Player', filter: false, sorter: true, key: 'player_name' },
  { label: 'Team', filter: false, sorter: false, key: 'team_name' },
  { label: 'Runs', filter: false, sorter: true, key: 'sum_rs' },
  { label: 'Fours', filter: false, sorter: true, key: 'sum_fours' },
  { label: 'Sixes', filter: false, sorter: true, key: 'sum_sixes' },
]

const BOWLING_COLUMNS = [
  { label: 'Player', filter: false, sorter: true, key: 'player_name' },
  { label: 'Team', filter: false, sorter: false, key: 'team_name' },
  { label: 'Overs', filter: false, sorter: true, key: 'sum_overs' },
  { label: 'Maidens', filter: false, sorter: true, key: 'sum_mdns' },
  { label: 'Wickets', filter: false, sorter: true, key: 'sum_wks' },
]

const FIELDING_COLUMNS = [
  { label: 'Player', filter: false, sorter: true, key: 'player_name' },
  { label: 'Team', filter: false, sorter: false, key: 'team_name' },
  { label: 'Catches', filter: false, sorter: true, key: 'sum_cs' },
  { label: 'Caught & Bowled/WK', filter: false, sorter: true, key: 'sum_cwks' },
  { label: 'Stumpings', filter: false, sorter: true, key: 'sum_sts' },
  { label: 'Run Outs (Direct)', filter: false, sorter: true, key: 'sum_rods' },
  { label: 'Run Outs (Assist)', filter: false, sorter: true, key: 'sum_roas' },
]

const SEGMENT_COLUMNS = {
  summary: SUMMARY_COLUMNS,
  batting: BATTING_COLUMNS,
  bowling: BOWLING_COLUMNS,
  fielding: FIELDING_COLUMNS,
}

const AllPlayerStatsTab = (props) => {
  const [segment, setSegment] = useState('summary')
  const [search, setSearch] = useState('')
  const [teamList, setTeamList] = useState([])
  const [positionList, setPositionList] = useState([])
  const [hiddenColumns, setHiddenColumns] = useState({})

  useEffect(() => {
    PlayerService.getTeamPositionValueBatBowlStyle().then((result) => {
      setTeamList(result.teamList || [])
      setPositionList(result.position || [])
    })
  }, [])

  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearch(value)
    props.setActivePage(1)
    props.setColumnFilter((prev) => ({ ...prev, player_name: value }))
  }

  const handleTeamChange = (e) => {
    props.setActivePage(1)
    props.setColumnFilter((prev) => ({ ...prev, team_id: e.target.value }))
  }

  const handlePositionChange = (e) => {
    props.setActivePage(1)
    props.setColumnFilter((prev) => ({ ...prev, position: e.target.value }))
  }

  const toggleColumn = (key) => {
    setHiddenColumns((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const columns = SEGMENT_COLUMNS[segment].filter((col) => !hiddenColumns[col.key])
  const stats = props.users?.stats || {}

  return (
    <>
      <CRow className="g-3 mb-3">
        <CCol sm={4}>
          <CCard className="h-100">
            <CCardBody className="d-flex justify-content-between align-items-center">
              <div>
                <div className="text-medium-emphasis small">Players</div>
                <div className="fs-4 fw-semibold">{stats.players ?? 0}</div>
              </div>
              <div
                className="d-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary"
                style={{ width: 42, height: 42 }}
              >
                <CIcon icon={cilPeople} size="lg" />
              </div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={4}>
          <CCard className="h-100">
            <CCardBody className="d-flex justify-content-between align-items-center">
              <div>
                <div className="text-medium-emphasis small">Total Fantasy Points</div>
                <div className="fs-4 fw-semibold">
                  {(stats.total_fantasy_points ?? 0).toLocaleString()}
                </div>
              </div>
              <div
                className="d-flex align-items-center justify-content-center rounded-circle bg-success-subtle text-success"
                style={{ width: 42, height: 42 }}
              >
                <CIcon icon={cilStar} size="lg" />
              </div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={4}>
          <CCard className="h-100">
            <CCardBody className="d-flex justify-content-between align-items-center">
              <div>
                <div className="text-medium-emphasis small">Top Performer</div>
                <div className="fw-semibold">
                  {stats.top_performer_name || '—'}
                  {stats.top_performer_name && (
                    <span className="text-medium-emphasis fw-normal">
                      {' '}
                      · {(stats.top_performer_points ?? 0).toLocaleString()} pts
                    </span>
                  )}
                </div>
              </div>
              <div
                className="d-flex align-items-center justify-content-center rounded-circle bg-warning-subtle text-warning"
                style={{ width: 42, height: 42 }}
              >
                <CIcon icon={cilBadge} size="lg" />
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <CInputGroup style={{ maxWidth: 260 }}>
          <CInputGroupText>
            <CIcon icon={cilSearch} />
          </CInputGroupText>
          <CFormInput placeholder="Search players" value={search} onChange={handleSearchChange} />
        </CInputGroup>
        <CFormSelect style={{ maxWidth: 180 }} onChange={handleTeamChange} defaultValue="">
          <option value="">All Teams</option>
          {teamList.map((team) => (
            <option value={team.id} key={team.id}>
              {team.name}
            </option>
          ))}
        </CFormSelect>
        <CFormSelect style={{ maxWidth: 180 }} onChange={handlePositionChange} defaultValue="">
          <option value="">All Positions</option>
          {positionList.map((position) => (
            <option value={position.id} key={position.id}>
              {position.name}
            </option>
          ))}
        </CFormSelect>
        <CButton
          color="light"
          className="d-inline-flex p-0 rounded-2 overflow-hidden border"
          style={{ gap: 0 }}
        >
          {SEGMENTS.map((s) => (
            <span
              key={s.key}
              role="button"
              onClick={() => setSegment(s.key)}
              className={`px-3 py-2 small ${
                segment === s.key ? 'bg-primary-subtle text-primary fw-semibold' : 'text-body'
              }`}
            >
              {s.label}
            </span>
          ))}
        </CButton>
        <CDropdown alignment="end" className="ms-auto">
          <CDropdownToggle color="secondary" variant="outline">
            <CIcon icon={cilColumns} className="me-1" />
            Columns
          </CDropdownToggle>
          <CDropdownMenu className="p-3">
            {SEGMENT_COLUMNS[segment].map((col) => (
              <div className="form-check" key={col.key}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`col-${col.key}`}
                  checked={!hiddenColumns[col.key]}
                  onChange={() => toggleColumn(col.key)}
                />
                <label className="form-check-label" htmlFor={`col-${col.key}`}>
                  {col.label}
                </label>
              </div>
            ))}
          </CDropdownMenu>
        </CDropdown>
      </div>

      <CCard>
        <CCardBody>
          <CSmartTable
            color="dark"
            columns={columns}
            columnSorter={{
              external: true,
            }}
            scopedColumns={{
              player_name: (item) => {
                const imgSrc = resolvePlayerImageUrl(item)
                return (
                  <td>
                    <div className="d-flex align-items-center">
                      {imgSrc && (
                        <img
                          src={imgSrc}
                          alt=""
                          width="32"
                          height="32"
                          className="rounded-circle me-2"
                        />
                      )}
                      <span className="fw-semibold">{item?.player_name}</span>
                    </div>
                  </td>
                )
              },
              team_name: (item) => <td>{item?.team_name || '—'}</td>,
              matches: (item) => <td>{item?.matches}</td>,
              current_value: (item) => {
                const change = (item?.current_value ?? 0) - (item?.initial_value ?? 0)
                return (
                  <td>
                    ${Number(item?.current_value ?? 0).toFixed(2)}m{' '}
                    {change > 0 && <CIcon icon={cilArrowTop} className="text-success" />}
                    {change < 0 && <CIcon icon={cilArrowBottom} className="text-danger" />}
                  </td>
                )
              },
              position_name: (item) => <td>{item?.position_name}</td>,
              sum_fp: (item) => (
                <td>
                  <CBadge color="primary">{item?.sum_fp}</CBadge>
                </td>
              ),
              sum_rs: (item) => <td>{item?.sum_rs}</td>,
              sum_fours: (item) => <td>{item?.sum_fours}</td>,
              sum_sixes: (item) => <td>{item?.sum_sixes}</td>,
              sum_overs: (item) => <td>{item?.sum_overs}</td>,
              sum_mdns: (item) => <td>{item?.sum_mdns}</td>,
              sum_cs: (item) => <td>{item?.sum_cs}</td>,
              sum_wks: (item) => <td>{item?.sum_wks}</td>,
              sum_cwks: (item) => <td>{item?.sum_cwks}</td>,
              sum_sts: (item) => <td>{item?.sum_sts}</td>,
              sum_rods: (item) => <td>{item?.sum_rods}</td>,
              sum_roas: (item) => <td>{item?.sum_roas}</td>,
              sum_dks: (item) => <td>{item?.sum_dks}</td>,
            }}
            items={props.users?.data}
            itemsPerPage={props.itemsPerPage}
            itemsPerPageSelect
            loading={props.loading}
            pagination={{
              external: true,
            }}
            paginationProps={{
              activePage: props.activePage,
              pages: Math.ceil(props.users?.total / props.itemsPerPage) || 1,
            }}
            tableProps={{
              hover: true,
              responsive: true,
              className: 'align-middle mb-0',
            }}
            onActivePageChange={(activePage) => props.setActivePage(activePage)}
            onItemsPerPageChange={(itemsPerPage) => {
              props.setActivePage(1)
              props.setItemsPerPage(itemsPerPage)
            }}
            onSorterChange={(sorter) => props.setColumnSorter(sorter)}
          />
        </CCardBody>
      </CCard>
    </>
  )
}

export default AllPlayerStatsTab
